import clsx from 'clsx'
import { AlertTriangle, Clock, Lock } from 'lucide-react'

/**
 * Status and severity badges.
 *
 * Every badge pairs colour with a word. Colour alone is not allowed to carry
 * meaning — roughly one in twelve men has a colour vision deficiency, and this
 * product is used by a predominantly male industrial workforce, so a red dot
 * that silently means "overdue" would be invisible to a meaningful share of
 * its users.
 */

const STATUS_TONES = {
  open:        { color: 'var(--color-status-open)',      label: 'Open' },
  acknowledged:{ color: 'var(--color-status-ack)',       label: 'Acknowledged' },
  in_progress: { color: 'var(--color-status-progress)',  label: 'In progress' },
  resolved:    { color: 'var(--color-status-resolved)',  label: 'Resolved' },
  closed:      { color: 'var(--color-status-closed)',    label: 'Closed' },
  reopened:    { color: 'var(--color-status-reopened)',  label: 'Reopened' },
  withdrawn:   { color: 'var(--color-status-withdrawn)', label: 'Withdrawn' },

  // The ball is in the employee's court — the officer is not late, and this is
  // deliberately not tinted like an overdue case.
  awaiting_employee: { color: 'var(--color-status-ack)', label: 'Awaiting reply' },

  // POSH
  registered:  { color: 'var(--color-status-open)',     label: 'Registered' },
  conciliation:{ color: 'var(--color-status-ack)',      label: 'Conciliation' },
  inquiry:     { color: 'var(--color-status-progress)', label: 'Inquiry' },
  hearing:     { color: 'var(--color-status-progress)', label: 'Hearing' },
  concluded:   { color: 'var(--color-status-resolved)', label: 'Concluded' },

  // Under appeal is not closed. It carries the reopened tone rather than the
  // resolved one, because the finding can still be set aside and the case is
  // emphatically not off the Committee's desk.
  on_appeal:   { color: 'var(--color-status-reopened)', label: 'Under appeal' },

  // Whistleblower
  received:      { color: 'var(--color-status-open)',      label: 'Received' },
  under_inquiry: { color: 'var(--color-status-progress)',  label: 'Under inquiry' },

  // Feedback
  new:          { color: 'var(--color-status-open)',      label: 'New' },
  under_review: { color: 'var(--color-status-progress)',  label: 'Under review' },
  implemented:  { color: 'var(--color-status-resolved)',  label: 'Implemented' },
  declined:     { color: 'var(--color-status-withdrawn)', label: 'Declined' },
  archived:     { color: 'var(--color-status-withdrawn)', label: 'Archived' },
}

/**
 * Lifecycle status.
 *
 * A tinted pill with a matching dot rather than a saturated block, so a table
 * of thirty rows does not become a wall of colour. The status word is always
 * present.
 */
export function StatusBadge({ status, label, className }) {
  const tone = STATUS_TONES[status] ?? { color: 'var(--color-ink-500)', label: status }

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5',
        'text-xs font-medium whitespace-nowrap',
        className,
      )}
      style={{
        color: tone.color,
        // The tint is mixed from the same token as the text, so a status colour
        // is defined in exactly one place. Changing a hue in index.css moves the
        // dot, the label and the background together.
        backgroundColor: `color-mix(in srgb, ${tone.color} 12%, transparent)`,
      }}
    >
      <span
        className="h-1.5 w-1.5 rounded-full shrink-0"
        style={{ backgroundColor: tone.color }}
        aria-hidden="true"
      />
      {label ?? tone.label}
    </span>
  )
}

const PRIORITY_TONES = {
  low:      { color: 'var(--color-ink-500)', label: 'Low' },
  medium:   { color: 'var(--color-info)',    label: 'Medium' },
  high:     { color: 'var(--color-warning)', label: 'High' },
  critical: { color: 'var(--color-danger)',  label: 'Critical' },
}

export function PriorityBadge({ priority, className }) {
  const tone = PRIORITY_TONES[priority] ?? PRIORITY_TONES.medium

  return (
    <span
      className={clsx('inline-flex items-center gap-1 text-xs font-medium whitespace-nowrap', className)}
      style={{ color: tone.color }}
    >
      {priority === 'critical' && <AlertTriangle size={12} aria-hidden="true" />}
      {tone.label}
    </span>
  )
}

/**
 * SLA state.
 *
 * Carries an icon as well as colour, because this is the single most
 * consequential signal in the whole queue: it is what tells an officer which
 * case to open first.
 */
export function SlaBadge({ hoursRemaining, breached, status, className }) {
  /*
   * A finished case reports history, not urgency.
   *
   * "Overdue" in a live queue means "act on this now", which is false for a
   * case that was closed last month. Terminal states get the past tense and a
   * muted treatment, so the red in this column always means something still
   * needs doing.
   */
  const finished = ['resolved', 'closed', 'withdrawn'].includes(status)

  if (finished) {
    if (!breached) return null

    return (
      <span
        className={clsx('inline-flex items-center gap-1 text-xs font-medium', className)}
        style={{ color: 'var(--color-ink-500)' }}
        title="This case was resolved after its deadline had passed"
      >
        <AlertTriangle size={11} aria-hidden="true" />
        Missed SLA
      </span>
    )
  }

  if (breached || (hoursRemaining !== null && hoursRemaining !== undefined && hoursRemaining < 0)) {
    const overdueBy = hoursRemaining != null ? Math.abs(hoursRemaining) : null

    return (
      <span
        className={clsx(
          'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-semibold',
          className,
        )}
        style={{
          color: 'var(--color-danger)',
          backgroundColor: 'var(--color-danger-bg)',
        }}
      >
        <AlertTriangle size={12} aria-hidden="true" />
        {overdueBy != null ? `${formatSpan(overdueBy)} overdue` : 'Overdue'}
      </span>
    )
  }

  if (hoursRemaining === null || hoursRemaining === undefined) return null

  // Inside a day is the window where an officer can still prevent a breach —
  // the only reason to draw attention to it.
  if (hoursRemaining <= 24) {
    return (
      <span
        className={clsx(
          'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium',
          className,
        )}
        style={{
          color: 'var(--color-warning)',
          backgroundColor: 'var(--color-warning-bg)',
        }}
      >
        <Clock size={12} aria-hidden="true" />
        {formatSpan(hoursRemaining)} left
      </span>
    )
  }

  return (
    <span className={clsx('text-xs text-[var(--color-ink-500)] tnum', className)}>
      {formatSpan(hoursRemaining)} left
    </span>
  )
}

function formatSpan(hours) {
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))}m`
  if (hours < 48) return `${Math.round(hours)}h`

  return `${Math.round(hours / 24)}d`
}

/**
 * Marks a screen or record as confidential.
 *
 * Used on POSH and whistleblower surfaces. Not decoration — an officer moving
 * between an ordinary grievance queue and a sealed case must never be in any
 * doubt about which they are looking at, because the handling rules and the
 * people permitted to see it are completely different.
 */
export function ConfidentialBadge({ label = 'Confidential', className }) {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 rounded px-2 py-0.5',
        'text-xs font-semibold uppercase tracking-wide',
        className,
      )}
      style={{
        color: 'var(--color-sealed)',
        backgroundColor: 'var(--color-sealed-bg)',
        border: '1px solid var(--color-sealed-line)',
      }}
    >
      <Lock size={11} aria-hidden="true" />
      {label}
    </span>
  )
}

/** Neutral label — channel, category, department. */
export function Chip({ children, tone = 'neutral', className }) {
  const tones = {
    neutral: 'bg-[var(--color-surface-sunken)] text-[var(--color-ink-700)] border-[var(--color-line)]',
    info:    'bg-[var(--color-info-bg)] text-[var(--color-info)] border-transparent',
    success: 'bg-[var(--color-success-bg)] text-[var(--color-success)] border-transparent',
    warning: 'bg-[var(--color-warning-bg)] text-[var(--color-warning)] border-transparent',
    danger:  'bg-[var(--color-danger-bg)] text-[var(--color-danger)] border-transparent',
  }

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs whitespace-nowrap',
        tones[tone] ?? tones.neutral,
        className,
      )}
    >
      {children}
    </span>
  )
}
